/* Free-shipping progress, derived from Shopify's own conversion rate.

   Background
   ----------
   The delivery profile grants free shipping at 150.00 in the SHOP currency
   (USD). Shopify converts a customer's cart back to shop currency using the
   rate it publishes as Shopify.currency.rate, so that rate - not a number we
   type in - is the only source of truth for what the threshold is worth in
   AUD, EUR, GBP and so on.

   A hardcoded local figure is wrong the moment the rate moves. This reads the
   live rate instead, so every currency stays correct with no maintenance and
   no per-currency table.

   Rounding is always UP to the next whole unit. If the exact threshold is
   AUD 218.60 we show AUD 219, so the bar can only ever understate how close
   the customer is. Telling someone they qualify when they do not is the one
   failure mode worth engineering against.

   Money is rendered through the store's own money format, passed in from
   Liquid as a sample string, so these figures look identical to every other
   price on the page. Intl.NumberFormat would say "A$219" where the rest of the
   store says "$219.00 AUD".

   Server-rendered markup states the shop-currency rule, which is true in every
   market, so the component degrades safely without JavaScript.
*/

if (!customElements.get('jpc-freeship')) {
  class JpcFreeship extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'text';
      this.shopThreshold = parseFloat(this.dataset.threshold || '150');
      this.total = parseInt(this.dataset.total || '0', 10);
      this.shipCost = this.dataset.shipCost || '';
      this.msgEl = this.querySelector('[data-fs-msg]');
      this.fillEl = this.querySelector('[data-fs-fill]');
      this.footEl = this.querySelector('[data-fs-foot]');

      this.parseMoneyFormat(this.dataset.moneyFormat || '');
      this.render();

      if (this.mode === 'bar' && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.refresh.bind(this));
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    /* Learn the store's money pattern from a rendered sample, e.g. the Liquid
       output of `999999 | money_with_currency` -> "$9,999.99 AUD". */
    parseMoneyFormat(sample) {
      this.fmt = null;
      var match = sample.match(/[\d.,\s]*\d/);
      if (!match) return;

      var raw = match[0];
      var lastDot = raw.lastIndexOf('.');
      var lastComma = raw.lastIndexOf(',');
      var sepIdx = Math.max(lastDot, lastComma);
      var decimals = 0;
      var decSep = '.';
      var groupSep = ',';

      if (sepIdx > -1 && raw.length - sepIdx - 1 === 2) {
        decimals = 2;
        decSep = raw.charAt(sepIdx);
        groupSep = decSep === '.' ? ',' : '.';
      }

      this.fmt = {
        prefix: sample.slice(0, match.index),
        suffix: sample.slice(match.index + raw.length),
        decimals: decimals,
        decSep: decSep,
        groupSep: groupSep
      };
    }

    get rate() {
      var r = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
      return r && isFinite(r) && r > 0 ? r : 1;
    }

    get code() {
      return (
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        this.dataset.currency ||
        'USD'
      );
    }

    get localThreshold() {
      return Math.ceil(this.shopThreshold * this.rate);
    }

    money(value) {
      if (!this.fmt) return this.code + ' ' + value.toFixed(2);
      var fixed = value.toFixed(this.fmt.decimals);
      var parts = fixed.split('.');
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.fmt.groupSep);
      var body = this.fmt.decimals ? intPart + this.fmt.decSep + parts[1] : intPart;
      return this.fmt.prefix + body + this.fmt.suffix;
    }

    async refresh() {
      try {
        var url = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js';
        var cart = await (await fetch(url, { headers: { Accept: 'application/json' } })).json();
        this.total = cart.total_price;
      } catch (e) {
        /* keep the last known total rather than showing something wrong */
      }
      this.render();
    }

    render() {
      var threshold = this.localThreshold;
      var total = this.total / 100;
      var remaining = threshold - total;

      if (this.mode !== 'bar') {
        if (this.msgEl) this.msgEl.textContent = 'Free shipping on orders over ' + this.money(threshold);
        return;
      }

      var pct = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 0;
      if (this.fillEl) this.fillEl.style.width = pct + '%';

      if (this.msgEl) {
        this.msgEl.textContent =
          remaining <= 0
            ? 'Your order qualifies for free shipping.'
            : 'You are ' + this.money(remaining) + ' away from free shipping.';
      }

      if (this.footEl) {
        this.footEl.textContent =
          (this.shipCost ? 'Flat ' + this.shipCost + ' tracked shipping, free at ' : 'Free shipping at ') +
          this.money(threshold) +
          '.';
      }

      this.classList.toggle('is-complete', remaining <= 0);
    }
  }

  customElements.define('jpc-freeship', JpcFreeship);
}
